import * as XLSX from 'xlsx'
import type { PersonalUsageData } from '~/services/usageDashboardService'
import { isUnlimited } from '~/utils/quotaConstants'
import type { DashboardData } from './types'

export interface ExcelExportMetadata {
  workspaceName?: string
  billingEmail?: string
  reportName?: string
}

/**
 * Formats a number for display (handles undefined/null)
 */
function formatNumber(value: number | undefined | null): string | number {
  if (value === undefined || value === null) return 'N/A'
  return value
}

/**
 * Formats credits limit (handles unlimited case)
 * - "N/A" = No data available (undefined)
 * - "Unlimited" = Unlimited quota (-1, null, or >= 999999)
 * - number = Actual limit value
 */
function formatCreditsLimit(value: number | undefined | null): string | number {
  if (value === undefined) return 'N/A'
  if (isUnlimited(value)) return 'Unlimited'
  return value
}

/**
 * Downloads dashboard data as an Excel file
 * @param data - The dashboard data to export
 * @param filename - The name of the Excel file (without extension)
 * @param metadata - Optional metadata for the report
 */
export function downloadDashboardAsExcel(
  data: DashboardData,
  filename: string = 'usage-dashboard-report',
  metadata?: ExcelExportMetadata
): void {
  const workbook = XLSX.utils.book_new()
  const generatedDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  // ============================================================================
  // Summary Sheet - All key metrics at a glance
  // ============================================================================
  const summaryRows: (string | number)[][] = [
    // Header
    ['USAGE DASHBOARD REPORT'],
    [],
    // Report Info
    ['Report Information'],
    ['Generated', generatedDate],
    ...(metadata?.workspaceName ? [['Workspace', metadata.workspaceName]] : []),
    ...(metadata?.billingEmail ? [['Billing Email', metadata.billingEmail]] : []),
    [],
    // Usage Overview
    ['Usage Overview'],
    ['Total Messages Exchanged', data.usage_overview.total_messages_exchanged],
    ['Tasks Run', data.usage_overview.tasks_run],
    [],
    // Category Breakdown
    ['Category Breakdown', 'Count', 'Percentage'],
    [
      'Coding',
      data.usage_overview.categories.coding.count,
      `${data.usage_overview.categories.coding.percentage}%`
    ],
    [
      'Testing',
      data.usage_overview.categories.testing.count,
      `${data.usage_overview.categories.testing.percentage}%`
    ],
    [
      'Documentation',
      data.usage_overview.categories.documentation.count,
      `${data.usage_overview.categories.documentation.percentage}%`
    ],
    [],
    // Credits
    ['Credits'],
    ['Credit Usage', formatNumber(data.credits.used)],
    ['Credits Limit', formatCreditsLimit(data.credits.total)],
    ['Credits Remaining', formatNumber(data.credits.remaining)],
    ...(data.credits.billable_flows_limit !== undefined
      ? [
          [],
          ['Billable Flows (Tasks)'],
          ['Billable Flows Usage', formatNumber(data.credits.billable_flows_used)],
          ['Billable Flows Limit', formatCreditsLimit(data.credits.billable_flows_limit)]
        ]
      : []),
    [],
    // Savings
    ['Value Generated'],
    ['Time Saved (hours)', data.savings.time_saved],
    ['Value Saved ($)', data.savings.value_saved],
    ['Since', data.savings.since],
    [],
    // Active Developers
    ['Team'],
    ['Active Developers', data.active_developers]
  ]

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows)
  // Set column widths for better readability
  summarySheet['!cols'] = [{ wch: 28 }, { wch: 20 }, { wch: 15 }]
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary')

  // ============================================================================
  // Daily Transactions Sheet
  // ============================================================================
  const totalMessages = data.weekly_chart.reduce((sum, item) => sum + item.messages_exchanged, 0)
  const totalTasks = data.weekly_chart.reduce((sum, item) => sum + item.tasks_run, 0)

  const dailyRows: (string | number)[][] = [
    ['DAILY TRANSACTIONS'],
    [],
    ['Date', 'Messages Exchanged', 'Tasks Run'],
    ...data.weekly_chart.map(item => [item.label, item.messages_exchanged, item.tasks_run]),
    [],
    ['TOTAL', totalMessages, totalTasks]
  ]

  const dailySheet = XLSX.utils.aoa_to_sheet(dailyRows)
  dailySheet['!cols'] = [{ wch: 15 }, { wch: 20 }, { wch: 12 }]
  XLSX.utils.book_append_sheet(workbook, dailySheet, 'Daily Transactions')

  // ============================================================================
  // Leaderboard Sheet
  // ============================================================================
  const leaderboardRows: (string | number)[][] = [
    ['TEAM LEADERBOARD'],
    [],
    ['Name', 'Messages', 'Tasks', 'Time Saved', 'Credit Usage'],
    ...data.leaderboard.map(row => [
      row.name,
      row.messages,
      row.tasks,
      row.time_saved,
      row.credit_used
    ])
  ]

  const leaderboardSheet = XLSX.utils.aoa_to_sheet(leaderboardRows)
  leaderboardSheet['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 10 }, { wch: 15 }, { wch: 14 }]
  XLSX.utils.book_append_sheet(workbook, leaderboardSheet, 'Leaderboard')

  // Generate and download
  const dateStr = new Date().toISOString().split('T')[0]
  XLSX.writeFile(workbook, `${filename}-${dateStr}.xlsx`)
}

// ============================================================================
// Personal Usage Report Functions
// ============================================================================

export interface PersonalUsageExportMetadata {
  workspaceName?: string
  billingEmail?: string
  reportName?: string
  userName?: string
}

/**
 * Downloads personal usage data as an Excel file
 * @param data - The personal usage data to export
 * @param filename - The name of the Excel file (without extension)
 * @param metadata - Optional metadata for the report
 */
export function downloadPersonalUsageAsExcel(
  data: PersonalUsageData,
  filename: string = 'personal-usage-report',
  metadata?: PersonalUsageExportMetadata
): void {
  const workbook = XLSX.utils.book_new()
  const generatedDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  // ============================================================================
  // Summary Sheet
  // ============================================================================
  const summaryRows: (string | number)[][] = [
    // Header
    ['PERSONAL USAGE REPORT'],
    [],
    // User Info
    ['User Information'],
    ['Name', metadata?.userName ?? data.name],
    ['Last Active', data.last_active],
    ...(metadata?.workspaceName ? [['Workspace', metadata.workspaceName]] : []),
    ...(metadata?.billingEmail ? [['Billing Email', metadata.billingEmail]] : []),
    ['Report Generated', generatedDate],
    [],
    // Usage Overview
    ['Usage Overview'],
    ['Total Messages Exchanged', data.usage_overview.total_messages_exchanged],
    ['Tasks Run', data.usage_overview.tasks_run],
    [],
    // Category Breakdown
    ['Category Breakdown', 'Count', 'Percentage'],
    [
      'Coding',
      data.usage_overview.categories.coding.count,
      `${data.usage_overview.categories.coding.percentage}%`
    ],
    [
      'Testing',
      data.usage_overview.categories.testing.count,
      `${data.usage_overview.categories.testing.percentage}%`
    ],
    [
      'Documentation',
      data.usage_overview.categories.documentation.count,
      `${data.usage_overview.categories.documentation.percentage}%`
    ],
    [],
    // Credits
    ['Credits'],
    ['Credit Usage', formatNumber(data.credits.used)],
    ['Credits Limit', formatCreditsLimit(data.credits.total)],
    ['Credits Remaining', formatNumber(data.credits.remaining)],
    [],
    // Savings
    ['Value Generated'],
    ['Time Saved (hours)', data.savings.time_saved],
    ['Value Saved ($)', data.savings.value_saved],
    ['Since', data.savings.since]
  ]

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows)
  summarySheet['!cols'] = [{ wch: 28 }, { wch: 20 }, { wch: 15 }]
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary')

  // ============================================================================
  // Recent Activities Sheet
  // ============================================================================
  if (data.recent_activities && data.recent_activities.length > 0) {
    const activitiesRows: (string | number)[][] = [
      ['RECENT ACTIVITIES'],
      [],
      ['Title', 'Type', 'Time', 'Messages'],
      ...data.recent_activities.map(activity => [
        activity.title,
        activity.type === 'task' ? 'Task' : 'Conversation',
        activity.time,
        activity.messages
      ])
    ]

    const activitiesSheet = XLSX.utils.aoa_to_sheet(activitiesRows)
    activitiesSheet['!cols'] = [{ wch: 50 }, { wch: 15 }, { wch: 20 }, { wch: 12 }]
    XLSX.utils.book_append_sheet(workbook, activitiesSheet, 'Recent Activities')
  }

  // Generate and download
  const dateStr = new Date().toISOString().split('T')[0]
  XLSX.writeFile(workbook, `${filename}-${dateStr}.xlsx`)
}
